// src/pages/LearnSection.tsx
import React, { useState } from 'react';
import {
    Box, Typography, Container, Divider, IconButton, useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    LocalLibraryOutlined, EmojiEventsOutlined, SavingsOutlined,
    TrendingUpOutlined, InfoOutlined, SchoolOutlined,
    QuizOutlined, LightbulbOutlined,
} from '@mui/icons-material';

/* ─── Brand ──────────────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    red:     '#FF4D6A',
    gold:    '#F5C842',
    orange:  '#FF9500',
};

/* ─── Content ────────────────────────────────────────────────────── */
const educationalContent = [
    {
        title: "How Your Credit Score Works",
        subtitle: "The fundamentals of credit in Nigeria",
        icon: TrendingUpOutlined,
        color: B.purple,
        points: [
            { label: "Payment History", body: "The most important factor. Paying loans and bills on time shows you're a responsible borrower." },
            { label: "Debt Amount", body: "How much you owe vs. your credit limits. Keeping utilization low is key to a healthy score." },
            { label: "Credit History Length", body: "The longer your history of responsible borrowing, the stronger your profile becomes." },
            { label: "New Credit Applications", body: "Applying for too much credit at once can temporarily dip your score." },
        ],
        intro: "Your credit score is your financial report card — a three-digit number calculated by licensed bureaus like CRC, CreditRegistry, and FirstCentral.",
    },
    {
        title: "Habits for Financial Health",
        subtitle: "Practical steps you can start today",
        icon: EmojiEventsOutlined,
        color: B.green,
        points: [
            { label: "Create a Budget", body: "Track your income and expenses to see exactly where your naira goes each month." },
            { label: "Pay Yourself First", body: "Before bills or shopping, set aside savings. Even small amounts compound significantly." },
            { label: "Needs vs. Wants", body: "Learning to distinguish necessities from impulse buys helps you live within your means." },
            { label: "Emergency Fund", body: "Aim for 3–6 months of living expenses saved. This is your safety net for life's surprises." },
        ],
        intro: "Building good money habits is a marathon, not a sprint. Small, consistent actions lead to lasting financial freedom.",
    },
    {
        title: "Savings vs. Investment",
        subtitle: "Knowing which tool to use when",
        icon: SavingsOutlined,
        color: B.gold,
        points: [
            { label: "Savings — Purpose", body: "Short-term goals like rent, gadgets, and your emergency fund. Low risk, easy access." },
            { label: "Savings — Returns", body: "Typically low, often below inflation. Safety and liquidity are the main benefits." },
            { label: "Investment — Purpose", body: "Long-term goals: retirement, property, children's education. Builds wealth over time." },
            { label: "Investment — Returns", body: "Potentially higher returns that can outpace inflation — but with varying levels of risk." },
        ],
        intro: "Saving and investing are both crucial tools. The key is knowing which one serves your goal and your timeline.",
    },
];

const faqs = [
    {
        question: "What is a credit score and why does it matter?",
        answer: "A credit score shows how reliable you are at repaying borrowed money. A good score unlocks better loan rates, credit cards, and mortgage opportunities from Nigerian banks and lenders — it's essentially your financial reputation.",
    },
    {
        question: "How does NG Credit help me build my score?",
        answer: "Through the Credit Builder feature, NG Credit reports your on-time payments to local credit bureaus. Consistent repayments build a positive credit history that grows your score over time.",
    },
    {
        question: "How long does building a good score take?",
        answer: "With regular on-time payments you can see positive movement within a few months, but establishing a solid, lender-trusted history typically takes 6–12 months of consistent behaviour.",
    },
    {
        question: "What happens if I miss a payment?",
        answer: "Payment history is the biggest score factor, so missed payments hurt. If you miss one, pay it off as soon as possible and stay consistent going forward. Recovery is very possible with time.",
    },
    {
        question: "Can I check my score inside the app?",
        answer: "Yes — navigate to your Dashboard at any time to see your current score, breakdown, and trend. Your score refreshes regularly as new data comes in.",
    },
];

/* ─── Sub-components ─────────────────────────────────────────────── */

const SectionHeader = ({ title, subtitle, icon: Icon, color, isDark }: any) => (
    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ color, fontSize: 18 }} />
        </Box>
        <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>{title}</Typography>
            <Typography sx={{ fontSize: '.66rem', color: alpha(isDark ? '#fff' : B.ink, .35) }}>{subtitle}</Typography>
        </Box>
    </Box>
);

/* ─── Main ───────────────────────────────────────────────────────── */
const LearnSection: React.FC = () => {
    const theme   = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark  = theme.palette.mode === 'dark';

    const [expandedLearn, setExpandedLearn] = useState<number | false>(0);
    const [expandedFaq,   setExpandedFaq]   = useState<number | false>(false);

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 4, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.accent, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.63rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: .4 }}>Financial Education</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-.03em', fontFamily: "'Instrument Serif', serif" }}>Wisdom Hub</Typography>
                    </Box>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .6, background: alpha(B.purple, .2), border: `1px solid ${alpha(B.light, .3)}`, borderRadius: 8, px: 1.25, py: .5 }}>
                        <SchoolOutlined sx={{ fontSize: 12, color: B.accent }} />
                        <Typography sx={{ color: B.accent, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.06em' }}>3 LESSONS</Typography>
                    </Box>
                </Box>

                {/* Hero stat strip */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                    {[
                        { label: 'Lessons',  value: '3',   sub: 'topics covered' },
                        { label: 'FAQs',     value: '5',   sub: 'answered'       },
                        { label: 'Read time', value: '2m', sub: 'avg per lesson' },
                    ].map(s => (
                        <Box key={s.label} sx={{ background: alpha('#fff', .07), border: `1px solid ${alpha('#fff', .1)}`, borderRadius: 2.5, px: 1.5, py: 1.25, backdropFilter: 'blur(8px)' }}>
                            <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: '1.5rem', lineHeight: 1, letterSpacing: '-.02em' }}>{s.value}</Typography>
                            <Typography sx={{ color: alpha('#fff', .4), fontSize: '.62rem', fontWeight: 600, mt: .35, letterSpacing: '.03em' }}>{s.sub}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ══ FLOATING CONTENT ═══════════════════════════════════ */}
            <Container maxWidth="sm" sx={{ mt: 3, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Lessons ── */}
                <Typography sx={{ fontWeight: 700, fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), letterSpacing: '.16em', textTransform: 'uppercase', mb: 1.5 }}>
                    Lessons
                </Typography>

                {educationalContent.map((item, index) => {
                    const Icon      = item.icon;
                    const isOpen    = expandedLearn === index;
                    return (
                        <Box key={index} sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>

                            {/* Header row — always visible, tappable */}
                            <Box onClick={() => setExpandedLearn(isOpen ? false : index)}
                                 sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: alpha(item.color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Icon sx={{ color: item.color, fontSize: 19 }} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>{item.title}</Typography>
                                        <Typography sx={{ fontSize: '.66rem', color: alpha(isDark ? '#fff' : B.ink, .35) }}>{item.subtitle}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ transition: 'transform .25s', transform: isOpen ? 'rotate(90deg)' : 'none', flexShrink: 0 }}>
                                    <ArrowForwardIosIcon sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .25) }} />
                                </Box>
                            </Box>

                            {/* Expanded body */}
                            {isOpen && (
                                <>
                                    {/* Intro text */}
                                    <Box sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`, pt: 2 }}>
                                        <Typography sx={{ fontSize: '.8rem', color: alpha(isDark ? '#fff' : B.ink, .55), lineHeight: 1.7, mb: 2.5 }}>
                                            {item.intro}
                                        </Typography>

                                        {/* Bullet points as styled rows */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                            {item.points.map((pt, pi) => (
                                                <Box key={pi} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: item.color, mt: .65, flexShrink: 0, boxShadow: `0 0 6px ${alpha(item.color, .5)}` }} />
                                                    <Box>
                                                        <Typography sx={{ fontSize: '.8rem', fontWeight: 700, color: isDark ? '#fff' : B.ink, mb: .2, letterSpacing: '-.01em' }}>{pt.label}</Typography>
                                                        <Typography sx={{ fontSize: '.76rem', color: alpha(isDark ? '#fff' : B.ink, .45), lineHeight: 1.6 }}>{pt.body}</Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Lesson number footer */}
                                    <Box sx={{ px: 2.5, py: 1.25, background: alpha(item.color, .06), borderTop: `1px solid ${alpha(item.color, .1)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), fontWeight: 600, letterSpacing: '.06em' }}>
                                            LESSON {index + 1} OF {educationalContent.length}
                                        </Typography>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, background: alpha(item.color, .12), border: `1px solid ${alpha(item.color, .2)}`, borderRadius: 8, px: 1, py: .35 }}>
                                            <LightbulbOutlined sx={{ fontSize: 11, color: item.color }} />
                                            <Typography sx={{ color: item.color, fontSize: '.6rem', fontWeight: 700, letterSpacing: '.04em' }}>KEY INSIGHT</Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    );
                })}

                {/* ── FAQs ── */}
                <Typography sx={{ fontWeight: 700, fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), letterSpacing: '.16em', textTransform: 'uppercase', mb: 1.5, mt: 1 }}>
                    FAQs
                </Typography>

                <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    {/* Section header */}
                    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(B.purple, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <QuizOutlined sx={{ color: B.purple, fontSize: 18 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Credit Building FAQs</Typography>
                            <Typography sx={{ fontSize: '.66rem', color: alpha(isDark ? '#fff' : B.ink, .35) }}>Tap a question to expand</Typography>
                        </Box>
                    </Box>

                    {/* FAQ rows */}
                    {faqs.map((faq, index) => {
                        const isOpen = expandedFaq === index;
                        return (
                            <React.Fragment key={index}>
                                <Box
                                    onClick={() => setExpandedFaq(isOpen ? false : index)}
                                    sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}
                                >
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: isOpen ? B.purple : alpha(isDark ? '#fff' : B.ink, .2), flexShrink: 0, transition: 'background .2s', boxShadow: isOpen ? `0 0 6px ${alpha(B.purple, .5)}` : 'none' }} />
                                        <Typography sx={{ fontSize: '.82rem', fontWeight: isOpen ? 600 : 500, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em', lineHeight: 1.4 }}>{faq.question}</Typography>
                                    </Box>
                                    <Box sx={{ transition: 'transform .2s', transform: isOpen ? 'rotate(90deg)' : 'none', flexShrink: 0 }}>
                                        <ArrowForwardIosIcon sx={{ fontSize: 11, color: isOpen ? B.purple : alpha(isDark ? '#fff' : B.ink, .25) }} />
                                    </Box>
                                </Box>

                                {isOpen && (
                                    <Box sx={{ px: 2.5, pb: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .05)}` }}>
                                        <Typography sx={{ fontSize: '.78rem', color: alpha(isDark ? '#fff' : B.ink, .5), lineHeight: 1.75, pt: 1.5 }}>
                                            {faq.answer}
                                        </Typography>
                                    </Box>
                                )}

                                {index < faqs.length - 1 && (
                                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </Box>

                {/* Footer */}
                <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <SchoolOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        NG CREDIT FINANCIAL EDUCATION
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
};

export default LearnSection;